import { Handle, NodeResizer, Position, useStore, useUpdateNodeInternals, type NodeProps } from '@xyflow/react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { DeviceInterfaces, DevicePort, DeviceType, NodeStatus, PortLayout } from '@ukrop-nm/shared';
import {
  DEFAULT_ZONE_COLOR,
  DEVICE_TYPE_LABELS_UK,
  DEVICE_NODE_MIN_HEIGHT,
  DEVICE_NODE_MIN_WIDTH,
  STATUS_COLORS,
  STATUS_LABELS_UK,
  gridAlignedPortPercent,
  hexToRgba,
  listNodePorts,
  resolvePortCount,
  resolveWifiAntennaCount,
} from '@ukrop-nm/shared';
import { DeviceIcon, StatusPattern } from './DeviceIcon';
import { useIconPack } from '../icons/IconPackContext';
import { usePortHover } from '../edges/PortHoverContext';
import { diagramStore } from '../state/diagramStore';
import {
  buildNodeTooltipContent,
  buildPortTooltipText,
} from '../tooltips/buildInfoTooltipText';
import { useDiagramTooltip, useInfoTooltipHandlers } from '../tooltips/DiagramTooltipContext';

export type UkropNodeData = {
  label: string;
  deviceType: DeviceType;
  status?: NodeStatus;
  locked?: boolean;
  monitorKey?: string;
  color?: string;
  portCount?: number;
  portLayout?: PortLayout;
  portProfileId?: string;
  wifiAntennaCount?: number;
  interfaces?: DeviceInterfaces;
  occupiedPorts?: string[];
  editable?: boolean;
};

function sideToPosition(side: DevicePort['side']): Position {
  switch (side) {
    case 'left':
      return Position.Left;
    case 'right':
      return Position.Right;
    case 'top':
      return Position.Top;
    case 'bottom':
      return Position.Bottom;
  }
}

function portStyle(
  port: DevicePort,
  busy: boolean,
  boxW: number,
  boxH: number,
): CSSProperties {
  const sideLen = port.side === 'left' || port.side === 'right' ? boxH : boxW;
  const pct = `${Math.round(gridAlignedPortPercent(sideLen, port.t) * 10) / 10}%`;
  const isWifi = port.kind === 'wifi';
  const isSfp = port.role === 'sfp';
  const freeColor = isWifi ? '#3b82f6' : isSfp ? '#f59e0b' : '#ef4444';
  const base: CSSProperties = {
    width: isWifi || isSfp ? 10 : 9,
    height: isWifi || isSfp ? 10 : 9,
    borderRadius: isWifi || isSfp ? '2px' : '50%',
    border: '1.5px solid #fff',
    background: busy ? '#22c55e' : freeColor,
    boxShadow: busy
      ? '0 0 0 1px #16a34a88'
      : isWifi
        ? '0 0 0 1px #1d4ed855'
        : isSfp
          ? '0 0 0 1px #b4530988'
          : '0 0 0 1px #b91c1c55',
  };
  if (port.side === 'left' || port.side === 'right') {
    return { ...base, top: pct };
  }
  return { ...base, left: pct };
}

export function DeviceNodeView({ id, data, selected }: NodeProps) {
  const { pack } = useIconPack();
  const { setHovered } = usePortHover();
  const { scheduleShow, hide, cancel } = useDiagramTooltip();
  const updateNodeInternals = useUpdateNodeInternals();
  const rootRef = useRef<HTMLDivElement>(null);
  const box = useStore((s) => {
    const n = s.nodes.find((node) => node.id === id);
    const w =
      Number(n?.measured?.width) ||
      Number(n?.width) ||
      (typeof n?.style?.width === 'number' ? n.style.width : 0) ||
      DEVICE_NODE_MIN_WIDTH;
    const h =
      Number(n?.measured?.height) ||
      Number(n?.height) ||
      (typeof n?.style?.height === 'number' ? n.style.height : 0) ||
      DEVICE_NODE_MIN_HEIGHT;
    return { w, h };
  });
  const d = data as UkropNodeData;

  const nodeTooltipText = useCallback(() => {
    const node = diagramStore.getSnapshot().nodes.find((n) => n.id === id);
    if (!node) return null;
    return buildNodeTooltipContent(node, {
      status: d.status,
      occupiedPorts: d.occupiedPorts,
    });
  }, [id, d.status, d.occupiedPorts]);

  const tipHandlers = useInfoTooltipHandlers(nodeTooltipText);

  const nodePorts = {
    type: d.deviceType,
    portCount: d.portCount,
    portLayout: d.portLayout,
    portProfileId: d.portProfileId,
    wifiAntennaCount: d.wifiAntennaCount,
    interfaces: d.interfaces,
  };
  const ports = d.deviceType === 'zone' ? [] : listNodePorts(nodePorts);
  const portKey = ports.map((p) => `${p.id}:${p.side}:${p.t}`).join('|');
  useEffect(() => {
    if (d.deviceType === 'zone') return;
    updateNodeInternals(id);
  }, [id, portKey, box.w, box.h, updateNodeInternals, d.deviceType]);

  if (d.deviceType === 'zone') {
    const zoneColor = d.color?.trim() || DEFAULT_ZONE_COLOR;
    const canResize = Boolean(d.editable && selected && !d.locked);
    return (
      <>
        {canResize ? (
          <NodeResizer
            minWidth={160}
            minHeight={100}
            keepAspectRatio={false}
            lineClassName="zone-resize-line"
            handleClassName="zone-resize-handle"
            onResizeEnd={(_event, params) => {
              diagramStore.updateNodes((list) =>
                list.map((n) =>
                  n.id === id
                    ? {
                        ...n,
                        size: {
                          width: Math.round(params.width),
                          height: Math.round(params.height),
                        },
                      }
                    : n,
                ),
              );
            }}
          />
        ) : null}
        <div
          className="zone-node"
          style={{
            outline: selected ? '2px solid #465fff' : undefined,
            background: hexToRgba(zoneColor, 0.035),
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: zoneColor,
          }}
          {...tipHandlers}
        >
          <div className="zone-node-chrome">
            <strong>{d.label}</strong>
            <div className="text-theme-xs text-gray-500 dark:text-gray-400">Зона</div>
          </div>
        </div>
      </>
    );
  }

  const color = d.status ? STATUS_COLORS[d.status] : '#e4e7ec';
  const ethCount = resolvePortCount(nodePorts);
  const wifiCount = resolveWifiAntennaCount(nodePorts);
  const occupied = new Set(d.occupiedPorts ?? []);
  const typeLabel = DEVICE_TYPE_LABELS_UK[d.deviceType];
  const statusLabel = d.status ? STATUS_LABELS_UK[d.status] : '';
  return (
    <div
      ref={rootRef}
      className="device-node"
      style={{
        borderColor: color,
        boxShadow: selected ? '0 0 0 2px #465fff55' : undefined,
        opacity: d.locked ? 0.85 : 1,
        width: '100%',
        height: '100%',
        minWidth: DEVICE_NODE_MIN_WIDTH,
        minHeight: DEVICE_NODE_MIN_HEIGHT,
      }}
      {...tipHandlers}
    >
      {ports.length === 0 ? (
        <>
          <Handle
            type="target"
            position={Position.Left}
            isConnectable={Boolean(d.editable && !d.locked)}
            className={d.editable && !d.locked ? 'device-port is-connectable is-free' : 'device-port'}
          />
          <Handle
            type="source"
            position={Position.Right}
            isConnectable={Boolean(d.editable && !d.locked)}
            className={d.editable && !d.locked ? 'device-port is-connectable is-free' : 'device-port'}
          />
        </>
      ) : (
        ports.map((port) => {
          const busy = occupied.has(port.id);
          const canConnect = Boolean(d.editable && !d.locked);
          return (
            <Handle
              key={port.id}
              id={port.id}
              type="source"
              position={sideToPosition(port.side)}
              isConnectable={canConnect}
              aria-label={
                canConnect
                  ? `Порт ${port.label}: прокласти звʼязок`
                  : `Порт ${port.label}`
              }
              className={`device-port${port.kind === 'wifi' ? ' is-wifi' : ''}${
                port.role === 'sfp' ? ' is-sfp' : ''
              }${busy ? ' is-busy' : ' is-free'}${canConnect ? ' is-connectable' : ''}`}
              style={portStyle(port, busy, box.w, box.h)}
              onMouseEnter={(e) => {
                e.stopPropagation();
                setHovered({ nodeId: id, portId: port.id });
                scheduleShow(
                  buildPortTooltipText(port.label, busy),
                  (e.currentTarget as HTMLElement).getBoundingClientRect(),
                );
              }}
              onMouseLeave={() => {
                setHovered(null);
                const text = nodeTooltipText();
                const el = rootRef.current;
                if (text && el) scheduleShow(text, el.getBoundingClientRect());
                else hide();
              }}
              onPointerDown={() => cancel()}
            />
          );
        })
      )}
      <DeviceIcon type={d.deviceType} pack={pack} size={34} />
      <div className="label">{d.label}</div>
      <div className="device-type">{typeLabel}</div>
      {ethCount > 0 || wifiCount > 0 ? (
        <div className="device-meta">
          {ethCount > 0
            ? `${ports.filter((p) => p.kind === 'ethernet' && occupied.has(p.id)).length}/${ethCount} Eth`
            : null}
          {ethCount > 0 && wifiCount > 0 ? ' · ' : null}
          {wifiCount > 0 ? `${wifiCount} Wi‑Fi` : null}
        </div>
      ) : null}
      {d.status ? (
        <div className="device-meta device-status">
          <StatusPattern status={d.status} /> {statusLabel}
        </div>
      ) : null}
    </div>
  );
}
